import { DOCS_FORM_NAMES, REQUEST_FORM_FIELDS } from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import {
  GetOntologyType,
  TransitionalCareFormData,
  TransitionalCareRequest,
} from "core/types";
import { format, fromUnixTime } from "date-fns";
import {
  assertUnreachable,
  formatDate,
} from "dsl/ecosystems/PatientForms/utils";
import { PDFForm } from "pdf-lib";
import { PdfFillerProps } from "..";

const _COMBINED_FIELDS = [
  "legal_representative_location_street_house_number",
  "legal_representative_location_zipcode_city",
  "patient_location_zipcode_city",
  "patient_location_street_house_number",
  "clinic_location_street_number",
  "clinic_location_zipcode_city",
] as const;

const combineTextFields = ({
  combined,
  first,
  form,
  formData,
  last,
}: {
  combined: (typeof _COMBINED_FIELDS)[number];
  first: keyof TransitionalCareFormData;
  form: PDFForm;
  formData: TransitionalCareFormData;
  last: keyof TransitionalCareFormData;
}) => {
  const firstValue = formData[first];
  const secondValue = formData[last];
  const text = [firstValue, secondValue].truthy().join(", ");

  if (!text.length) return;

  form.getTextField(combined).setText(text);
};

const formatDateTime = (timestamp: number, time: number, locale: Locale) =>
  `${formatDate(timestamp, locale)} ${format(fromUnixTime(time), "HH:mm", {
    locale,
  })}`;

function requestSentTimeToNumber(
  time:
    | number
    | { hour: number; millisecond: number; minute: number; second: number }
    | null
    | undefined,
): number | null {
  if (time == null) return null;
  if (typeof time === "number") return time;
  return (
    time.hour * 3600 + time.minute * 60 + time.second + time.millisecond / 1000
  );
}

function getRequestStatusKey(request: TransitionalCareRequest): number | null {
  const status = request[REQUEST_FORM_FIELDS.REQUEST_STATUS];
  if (status == null) return null;
  return Array.isArray(status) ? status[0] ?? null : status;
}

const setRequest =
  ({
    form,
    getOntology,
    locale,
  }: {
    form: PDFForm;
    getOntology: GetOntologyType;
    locale: Locale;
  }) =>
  (request: TransitionalCareRequest, i: number) => {
    const fieldIndex = i + 1;
    const careproviderField = form.getTextField(
      `careprovider_details_${fieldIndex}`,
    );

    const {
      careprovider_address_address,
      careprovider_address_city,
      careprovider_address_zipcode,
      careprovider_name,
      request_sent_date,
      request_sent_time,
    } = request;

    careproviderField.setText(
      `${careprovider_name}, ${careprovider_address_address}, ${careprovider_address_city} ${careprovider_address_zipcode}`,
    );

    const dateField = form.getTextField(`request_sent_date_${fieldIndex}`);
    const timeNumber = requestSentTimeToNumber(request_sent_time);
    const date =
      timeNumber != null
        ? formatDateTime(request_sent_date!, timeNumber, locale)
        : "";
    dateField.setText(date);

    const statusField = form.getTextField(`request_status_${fieldIndex}`);
    const status = getOntology({
      key: getRequestStatusKey(request),
      type: ONTOLOGIES.TRANSITIONAL_CARE_REQUEST_STATUS,
    });
    statusField.setText(status);
  };

export const TransitionalCareFormMapping = ({
  form,
  formData,
  getOntology,
  locale,
}: PdfFillerProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  Object.keys(formData).forEach((key) => {
    try {
      const field = key as keyof TransitionalCareFormData;
      const fieldValue = formData[field];

      switch (field) {
        // DATE FIELDS
        case "handover_to_insurance_date":
        case "insurance_form_sent_date":
        case "transitional_care_start_date":
        case "transitional_care_start_search_date":
        case "transitional_care_patient_confirmation_date":
        case "aftercare_start_date":
        case "aftercare_application_date":
        case "patient_date_of_birth":
        case "prior_treatment_end_date":
        case "aftercare_care_level_applied_date":
        case "prior_treatment_start_date":
        case "transitional_care_end_date": {
          if (!fieldValue) break;

          const date = formatDate(fieldValue as number, locale);
          form.getTextField(field).setText(date);

          break;
        }
        // COMBINED TEXT FIELDS
        case "legal_representative_location_street":
          break;
        case "legal_representative_location_street_number": {
          combineTextFields({
            combined: "legal_representative_location_street_house_number",
            first: "legal_representative_location_street",
            form,
            formData,
            last: "legal_representative_location_street_number",
          });
          break;
        }
        case "legal_representative_location_zipcode":
          break;
        case "legal_representative_location_city": {
          combineTextFields({
            combined: "legal_representative_location_zipcode_city",
            first: "legal_representative_location_zipcode",
            form,
            formData,
            last: "legal_representative_location_city",
          });
          break;
        }
        case "patient_location_street":
          break;
        case "patient_location_house_number": {
          combineTextFields({
            combined: "patient_location_street_house_number",
            first: "patient_location_street",
            form,
            formData,
            last: "patient_location_house_number",
          });
          break;
        }
        case "patient_location_zipcode":
        case "patient_location_city": {
          combineTextFields({
            combined: "patient_location_zipcode_city",
            first: "patient_location_zipcode",
            form,
            formData,
            last: "patient_location_city",
          });
          break;
        }
        case "clinic_location_street":
          break;
        case "clinic_location_street_number": {
          combineTextFields({
            combined: "clinic_location_street_number",
            first: "clinic_location_street",
            form,
            formData,
            last: "clinic_location_street_number",
          });
          break;
        }
        case "clinic_location_zipcode":
          break;
        case "clinic_location_city": {
          combineTextFields({
            combined: "clinic_location_zipcode_city",
            first: "clinic_location_zipcode",
            form,
            formData,
            last: "clinic_location_city",
          });
          break;
        }
        // CHECKBOX RADIOS
        case "aftercare_care_level_applied":
        case "transitional_care_suitable_providers_more_than_twenty":
        case "aftercare_care_level_available":
        case "internet_portal_used":
        case "aftercare_mobile":
        case "aftercare_medical_rehab":
        case "aftercare_application_submitted":
        case "aftercare_shortterm_care":
        case "legal_representative_consent_provided":
        case "transitional_care_patient_confirmation":
        case "aftercare_care_level_aftercare_required": {
          const pdfField = form.getRadioGroup(field);

          pdfField.clear();

          pdfField.select(fieldValue ? "yes" : "no");

          break;
        }
        // RADIO GROUP
        case "care_necessity": {
          const pdfField = form.getRadioGroup(field);

          pdfField.clear();

          if (fieldValue) {
            pdfField.select(fieldValue.toString());
          }

          break;
        }
        // REQUEST LIST
        case "request_list": {
          if (!Array.isArray(fieldValue)) {
            break;
          }
          fieldValue.forEach(setRequest({ form, getOntology, locale }));
          break;
        }
        // NUMBER FIELDS
        case "transitional_care_suitable_providers_count": {
          if (fieldValue == null) break;

          form.getTextField(field).setText(fieldValue.toString());
          break;
        }
        // TEXT FIELDS
        case "aftercare_application_explanation":
        case "aftercare_care_level_explanation":
        case "clinic_ik_number":
        case "clinic_name":
        case "legal_representative_first_name":
        case "legal_representative_last_name":
        case "patient_first_name":
        case "patient_insurance_number":
        case "patient_last_name":
        case "prior_treatment_hospital_id":
        case "social_worker_email":
        case "social_worker_fax":
        case "social_worker_first_name":
        case "social_worker_last_name":
        case "social_worker_telephone":
        case "transitional_care_department_code":
        case "transitional_care_description":
        case "transitional_care_location_id":
        case "transitional_care_patient_confirmation_explanation": {
          if (fieldValue == null) break;

          if (typeof fieldValue !== "string") {
            console.warn("Unhandled field type", {
              field,
              value: fieldValue,
            });
            break;
          }

          form.getTextField(field).setText(fieldValue);
          break;
        }
        default: {
          // ensure all form data keys are handled in the pdf
          assertUnreachable(field);
        }
      }
    } catch (err) {
      const message = `Error filling PDF field: ${key}`;
      console.error(message, err);
      throw new Error(message);
    }
  });
};
